import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { BookingState, BookingContextType, Property } from '../types';

const initialState: BookingState = {
  checkIn: null,
  checkOut: null,
  guests: 5,
  selectedProperty: null,
  totalPrice: 0,
};

type BookingAction =
  | { type: 'SET_CHECK_IN'; payload: Date }
  | { type: 'SET_CHECK_OUT'; payload: Date }
  | { type: 'SET_GUESTS'; payload: number }
  | { type: 'SET_PROPERTY'; payload: Property | null }
  | { type: 'CALCULATE_TOTAL' }
  | { type: 'CLEAR_BOOKING' };

function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case 'SET_CHECK_IN':
      return { ...state, checkIn: action.payload };
    
    case 'SET_CHECK_OUT':
      return { ...state, checkOut: action.payload };
    
    case 'SET_GUESTS':
      return { ...state, guests: action.payload };
    
    case 'SET_PROPERTY':
      return { ...state, selectedProperty: action.payload };
    
    case 'CALCULATE_TOTAL':
      if (!state.selectedProperty || !state.checkIn || !state.checkOut) {
        return state;
      }
      
      const nights = Math.ceil(
        (state.checkOut.getTime() - state.checkIn.getTime()) / (1000 * 60 * 60 * 24)
      );
      const totalPrice = nights * state.selectedProperty.price;
      
      return { ...state, totalPrice };
    
    case 'CLEAR_BOOKING':
      return initialState;
    
    default:
      return state;
  }
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  const setCheckIn = (date: Date) => {
    dispatch({ type: 'SET_CHECK_IN', payload: date });
    if (state.checkOut) {
      dispatch({ type: 'CALCULATE_TOTAL' });
    }
  };

  const setCheckOut = (date: Date) => {
    dispatch({ type: 'SET_CHECK_OUT', payload: date });
    if (state.checkIn) {
      dispatch({ type: 'CALCULATE_TOTAL' });
    }
  };

  const setGuests = (guests: number) => {
    dispatch({ type: 'SET_GUESTS', payload: guests });
  };

  const setSelectedProperty = (property: Property | null) => {
    dispatch({ type: 'SET_PROPERTY', payload: property });
  };

  const calculateTotalPrice = () => {
    dispatch({ type: 'CALCULATE_TOTAL' });
  };

  const clearBooking = () => {
    dispatch({ type: 'CLEAR_BOOKING' });
  };

  const value: BookingContextType = {
    state,
    setCheckIn,
    setCheckOut,
    setGuests,
    setSelectedProperty,
    calculateTotalPrice,
    clearBooking,
  };

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};
